/**
 * Lazy File Loader Service
 * Downloads SharePoint file content on-demand when user opens a document
 */

import { auth } from '../config/firebase';
import { sharepointConfig } from '../config/sharepoint';
import { fileCache } from './file-cache';

/**
 * Get Firebase auth token
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
}

/**
 * Download file content for a SharePoint placeholder file
 * Uses cache if available, otherwise downloads from SharePoint
 */
export async function loadSharePointFileContent(file: File): Promise<Blob> {
  const metadata = (file as any).__sharepointMetadata;
  
  if (!metadata) {
    throw new Error('File is not a SharePoint file');
  }

  const { downloadUrl, path } = metadata;

  // Check cache first
  const cached = fileCache.get(path);
  if (cached) {
    console.log(`📦 Using cached content for: ${file.name}`);
    return cached;
  }

  // Download from SharePoint
  console.log(`⬇️ Downloading from SharePoint: ${file.name} (${formatSize(metadata.size)})`);
  
  const token = await getAuthToken();
  
  const response = await fetch(`${sharepointConfig.apiEndpoint}/downloadSharePointFile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ downloadUrl }),
  });

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }

  const blob = await response.blob();
  console.log(`✅ Downloaded: ${file.name} (${formatSize(blob.size)})`);

  // Cache for future use
  fileCache.set(path, blob);

  return blob;
}

/**
 * Check if a file is a SharePoint placeholder
 */
export function isSharePointFile(file: File): boolean {
  return !!(file as any).__sharepointMetadata;
}

/**
 * Get SharePoint file metadata
 */
export function getSharePointMetadata(file: File): { downloadUrl: string; size: number; path: string } | null {
  return (file as any).__sharepointMetadata || null;
}

/**
 * Format file size
 */
function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  const gb = bytes / (1024 * 1024 * 1024);
  
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(2)} KB`;
}










